import React, { useState, useEffect } from 'react';
import {
    FiPlus,
    FiRefreshCw,
    FiCheckCircle,
    FiXCircle,
    FiDatabase,
    FiChevronRight,
    FiToggleLeft,
    FiToggleRight,
    FiSearch,
    FiLayers
} from 'react-icons/fi';
import {
    getAllDepartmentsAdmin,
    getAllSpecialtiesByDepartmentAdmin,
    seedTaxonomy,
    addDepartment,
    addSpecialty,
    toggleDepartmentActive,
    toggleSpecialtyActive,
    updateDepartmentOrder
} from './taxonomyService';
import '../doctors/DoctorList.css';

export default function TaxonomyManagement() {
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSeeding, setIsSeeding] = useState(false);

    // New Add States
    const [showAddDept, setShowAddDept] = useState(false);
    const [showAddSpec, setShowAddSpec] = useState(false);
    const [newDeptName, setNewDeptName] = useState('');
    const [newSpecName, setNewSpecName] = useState('');

    useEffect(() => {
        loadDepartments();
    }, []);

    useEffect(() => {
        if (selectedDept) {
            loadSpecialties(selectedDept.id);
        }
    }, [selectedDept]);

    async function loadDepartments() {
        try {
            setLoading(true);
            const data = await getAllDepartmentsAdmin();
            setDepartments(data);
        } catch (err) {
            setError('Failed to load departments');
        } finally {
            setLoading(false);
        }
    }

    async function loadSpecialties(deptId) {
        try {
            setLoading(true);
            const data = await getAllSpecialtiesByDepartmentAdmin(deptId);
            setSpecialties(data);
        } catch (err) {
            setError('Failed to load specialties');
        } finally {
            setLoading(false);
        }
    }

    async function handleToggleDept(dept) {
        try {
            setLoading(true);
            setError('');
            await toggleDepartmentActive(dept.id, dept.active !== false);
            setSuccess(`Department "${dept.name}" ${dept.active !== false ? 'disabled' : 'enabled'} successfully.`);
            await loadDepartments();
            // If currently selected dept was toggled, refresh specialties too
            if (selectedDept?.id === dept.id) {
                setSelectedDept(prev => ({ ...prev, active: !(prev.active !== false) }));
            }
        } catch (err) {
            setError('Failed to toggle department status');
        } finally {
            setLoading(false);
        }
    }

    async function handleToggleSpec(spec) {
        try {
            setLoading(true);
            setError('');
            await toggleSpecialtyActive(spec.id, spec.active !== false);
            setSuccess(`Specialty "${spec.name}" ${spec.active !== false ? 'disabled' : 'enabled'} successfully.`);
            if (selectedDept) await loadSpecialties(selectedDept.id);
        } catch (err) {
            setError('Failed to toggle specialty status');
        } finally {
            setLoading(false);
        }
    }

    async function handleSeed() {
        if (!window.confirm('This will reset and initialize the default medical categories. Continue?')) return;

        try {
            setIsSeeding(true);
            setError('');
            setSelectedDept(null);
            setSpecialties([]);
            await seedTaxonomy();
            setSuccess('Medical Categories refreshed successfully!');
            await loadDepartments();
        } catch (err) {
            setError('Seeding failed: ' + err.message);
        } finally {
            setIsSeeding(false);
        }
    }

    async function handleAddDept(e) {
        e.preventDefault();
        if (!newDeptName.trim()) return;

        try {
            setLoading(true);
            await addDepartment({
                name: newDeptName.trim(),
                order: departments.length + 1
            });
            setNewDeptName('');
            setShowAddDept(false);
            setSuccess('Department added!');
            await loadDepartments();
        } catch (err) {
            setError('Failed to add department');
        } finally {
            setLoading(false);
        }
    }

    async function handleAddSpec(e) {
        e.preventDefault();
        if (!newSpecName.trim() || !selectedDept) return;

        try {
            setLoading(true);
            await addSpecialty({
                name: newSpecName.trim(),
                departmentId: selectedDept.id
            });
            setNewSpecName('');
            setShowAddSpec(false);
            setSuccess('Specialty added!');
            await loadSpecialties(selectedDept.id);
        } catch (err) {
            setError('Failed to add specialty');
        } finally {
            setLoading(false);
        }
    }

    async function handleMoveDept(dept, direction) {
        const index = departments.findIndex(d => d.id === dept.id);
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === departments.length - 1) return;

        const otherDept = departments[direction === 'up' ? index - 1 : index + 1];

        try {
            setLoading(true);
            await updateDepartmentOrder(dept.id, otherDept.order);
            await updateDepartmentOrder(otherDept.id, dept.order);
            setSuccess('Order updated');
            await loadDepartments();
        } catch (err) {
            setError('Failed to reorder');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-container">
            <div className="page-header" style={{ marginBottom: '32px' }}>
                <div className="header-left">
                    <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '4px' }}>
                        Medical Categories
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '15px' }}>
                        Configure the medical hierarchy by managing departments and their corresponding specialties.
                    </p>
                </div>
                <div className="header-actions">
                    {departments.length === 0 ? (
                        <button
                            className="btn-primary"
                            onClick={handleSeed}
                            disabled={isSeeding}
                            style={{ padding: '10px 20px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}
                        >
                            <FiDatabase style={{ fontSize: '18px' }} />
                            <span style={{ fontWeight: 600 }}>{isSeeding ? 'Initializing...' : 'Initialize Hierarchy'}</span>
                        </button>
                    ) : (
                        <button
                            className="btn-secondary"
                            onClick={handleSeed}
                            disabled={isSeeding}
                            style={{ padding: '8px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', borderColor: '#e2e8f0' }}
                            title="Reset to default medical taxonomy"
                        >
                            <FiRefreshCw className={isSeeding ? 'spin-icon' : ''} />
                            <span>Reset to Defaults</span>
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="error-message" style={{ padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FiXCircle style={{ fontSize: '20px' }} />
                    <span style={{ fontWeight: 500 }}>{error}</span>
                </div>
            )}

            {success && (
                <div className="success-message" style={{ padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                    <FiCheckCircle style={{ fontSize: '20px' }} />
                    <span style={{ fontWeight: 500 }}>{success}</span>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '32px', alignItems: 'start' }}>

                {/* DEPARTMENTS CARD */}
                <div className="card" style={{ padding: '0', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' }}>
                    <div className="card-header" style={{
                        padding: '24px',
                        borderBottom: '1px solid #f1f5f9',
                        background: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Departments</h3>
                            <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>{departments.length} Medical Units</span>
                        </div>
                        <button
                            onClick={() => setShowAddDept(true)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                color: '#475569',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                        >
                            <FiPlus /> New
                        </button>
                    </div>

                    <div style={{ padding: '16px' }}>
                        {showAddDept && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '16px',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px dashed #cbd5e1'
                            }}>
                                <form onSubmit={handleAddDept} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <input
                                        type="text"
                                        placeholder="Enter Department Name..."
                                        value={newDeptName}
                                        onChange={(e) => setNewDeptName(e.target.value)}
                                        autoFocus
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            fontSize: '14px',
                                            outline: 'none',
                                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button type="submit" className="btn-primary" style={{ padding: '6px 16px', borderRadius: '6px', fontSize: '13px' }}>Add Department</button>
                                        <button type="button" className="btn-secondary" onClick={() => setShowAddDept(false)} style={{ padding: '6px 16px', borderRadius: '6px', fontSize: '13px', border: 'none' }}>Cancel</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {loading && departments.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <FiRefreshCw className="spin-icon" style={{ fontSize: '24px', color: '#94a3b8' }} />
                                <p style={{ marginTop: '12px', color: '#94a3b8', fontSize: '14px' }}>Loading departments...</p>
                            </div>
                        ) : (
                            <div className="dept-list" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {departments.map(dept => (
                                    <div
                                        key={dept.id}
                                        onClick={() => setSelectedDept(dept)}
                                        style={{
                                            padding: '14px 16px',
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            background: selectedDept?.id === dept.id ? 'linear-gradient(90deg, #f1f5f9 0%, #fff 100%)' : 'transparent',
                                            borderLeft: selectedDept?.id === dept.id ? '4px solid #10b981' : '4px solid transparent',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            boxShadow: selectedDept?.id === dept.id ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                        }}
                                        onMouseOver={(e) => { if (selectedDept?.id !== dept.id) e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseOut={(e) => { if (selectedDept?.id !== dept.id) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '8px',
                                                background: dept.active ? (selectedDept?.id === dept.id ? '#10b981' : '#f1f5f9') : '#e2e8f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: dept.active ? (selectedDept?.id === dept.id ? '#fff' : '#64748b') : '#94a3b8',
                                                fontSize: '12px',
                                                fontWeight: 700,
                                                opacity: dept.active ? 1 : 0.6
                                            }}>
                                                {dept.name.charAt(0)}
                                            </div>
                                            <span style={{
                                                fontWeight: selectedDept?.id === dept.id ? 700 : 500,
                                                color: dept.active ? (selectedDept?.id === dept.id ? '#0f172a' : '#475569') : '#94a3b8',
                                                fontSize: '14px',
                                                textDecoration: dept.active ? 'none' : 'line-through',
                                                opacity: dept.active ? 1 : 0.6
                                            }}>
                                                {dept.name}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleToggleDept(dept); }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: dept.active !== false ? '#10b981' : '#cbd5e1',
                                                    padding: '4px',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title={dept.active !== false ? 'Disable Department' : 'Enable Department'}
                                            >
                                                {dept.active !== false ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                                            </button>
                                            <div className="reorder-controls" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleMoveDept(dept, 'up'); }}
                                                    disabled={departments.indexOf(dept) === 0}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '10px', padding: '0 4px', opacity: departments.indexOf(dept) === 0 ? 0 : 1 }}
                                                    title="Move Up"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleMoveDept(dept, 'down'); }}
                                                    disabled={departments.indexOf(dept) === departments.length - 1}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '10px', padding: '0 4px', opacity: departments.indexOf(dept) === departments.length - 1 ? 0 : 1 }}
                                                    title="Move Down"
                                                >
                                                    ▼
                                                </button>
                                            </div>
                                            <FiChevronRight style={{
                                                opacity: selectedDept?.id === dept.id ? 1 : 0.2,
                                                color: selectedDept?.id === dept.id ? '#10b981' : '#64748b',
                                                transform: selectedDept?.id === dept.id ? 'translateX(4px)' : 'none',
                                                transition: 'all 0.2s'
                                            }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* SPECIALTIES CARD */}
                <div className="card" style={{ padding: '0', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)', backgroundColor: '#fff', borderRadius: '16px', minHeight: '500px' }}>

                    {!selectedDept ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px', textAlign: 'center' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '24px',
                                background: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '24px'
                            }}>
                                <FiSearch size={40} style={{ color: '#cbd5e1' }} />
                            </div>
                            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>No Department Selected</h3>
                            <p style={{ color: '#64748b', maxWidth: '300px', lineHeight: 1.6 }}>Choose a department from the sidebar to view and manage its specific medical specialties.</p>
                        </div>
                    ) : (
                        <>
                            <div className="card-header" style={{
                                padding: '32px',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                            }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                                        <FiLayers /> Focused Unit
                                    </div>
                                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#0f172a' }}>{selectedDept.name}</h2>
                                    <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Managing {specialties.length} specializations in this department.</p>
                                </div>
                                <button
                                    className="btn-primary"
                                    onClick={() => setShowAddSpec(true)}
                                    style={{
                                        borderRadius: '10px',
                                        padding: '10px 18px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        boxShadow: '0 4px 10px rgba(16, 185, 129, 0.15)'
                                    }}
                                >
                                    <FiPlus /> New Specialty
                                </button>
                            </div>

                            <div style={{ padding: '32px' }}>
                                {showAddSpec && (
                                    <div style={{
                                        marginBottom: '32px',
                                        padding: '24px',
                                        background: '#fafafa',
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.02)'
                                    }}>
                                        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#1e293b' }}>Add New Specialization</h4>
                                        <form onSubmit={handleAddSpec} style={{ display: 'flex', gap: '12px' }}>
                                            <input
                                                type="text"
                                                placeholder="e.g., Pediatric Oncology"
                                                value={newSpecName}
                                                onChange={(e) => setNewSpecName(e.target.value)}
                                                autoFocus
                                                style={{
                                                    flex: 1,
                                                    padding: '12px 16px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '15px',
                                                    outline: 'none'
                                                }}
                                            />
                                            <button type="submit" className="btn-primary" style={{ padding: '0 24px', borderRadius: '10px' }}>Save Specialty</button>
                                            <button type="button" className="btn-secondary" onClick={() => setShowAddSpec(false)} style={{ border: 'none' }}>Cancel</button>
                                        </form>
                                    </div>
                                )}

                                {loading && specialties.length === 0 ? (
                                    <div style={{ padding: '60px', textAlign: 'center' }}>
                                        <FiRefreshCw className="spin-icon" style={{ fontSize: '32px', color: '#10b981' }} />
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                                        {specialties.map(spec => (
                                            <div
                                                key={spec.id}
                                                style={{
                                                    padding: '20px',
                                                    border: '1px solid #f1f5f9',
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    background: spec.active ? '#fff' : '#f8fafc',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                                    opacity: spec.active ? 1 : 0.7
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <span style={{
                                                        fontSize: '15px',
                                                        fontWeight: 600,
                                                        color: spec.active ? '#334155' : '#94a3b8',
                                                        display: 'block',
                                                        textDecoration: spec.active ? 'none' : 'line-through'
                                                    }}>
                                                        {spec.name}
                                                    </span>
                                                    <span style={{ fontSize: '11px', color: '#cbd5e1' }}>ID: {spec.id}</span>
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleToggleSpec(spec); }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: spec.active !== false ? '#10b981' : '#cbd5e1',
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    title={spec.active ? 'Disable Specialty' : 'Enable Specialty'}
                                                >
                                                    {spec.active ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
                                                </button>
                                            </div>
                                        ))}

                                        {specialties.length === 0 && !loading && (
                                            <div style={{ gridColumn: '1 / -1', padding: '60px', textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                                                <FiLayers style={{ fontSize: '32px', color: '#cbd5e1', marginBottom: '16px' }} />
                                                <p style={{ color: '#64748b', fontWeight: 500 }}>No specialties found in this unit.</p>
                                                <button onClick={() => setShowAddSpec(true)} style={{ color: '#10b981', background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', marginTop: '8px' }}>
                                                    + Add the first one
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
